import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Megaphone, IceCream2, ChevronDown, ZoomIn, RotateCcw } from "lucide-react";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { ASSETS } from "@/assets";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export default function HeroMap() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/public/cart-info").then((res) => setInfo(res.data)).catch(() => {});
  }, []);

  // --- Mobile pinch-zoom / pan ---
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [t, setT] = useState({ scale: 1, x: 0, y: 0 });
  const gesture = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const dims = useCallback(() => {
    const w = wrapRef.current, inn = innerRef.current;
    if (!w || !inn) return null;
    return { ww: w.clientWidth, wh: w.clientHeight, bw: inn.offsetWidth, bh: inn.offsetHeight };
  }, []);

  const clampPan = useCallback((scale, x, y) => {
    const d = dims();
    if (!d) return { x, y };
    const cw = d.bw * scale, ch = d.bh * scale;
    const minX = Math.min(0, d.ww - cw), minY = Math.min(0, d.wh - ch);
    return { x: clamp(x, minX, 0), y: clamp(y, minY, 0) };
  }, [dims]);

  useLayoutEffect(() => {
    setT({ scale: 1, x: 0, y: 0 });
  }, [isMobile]);

  const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const mid = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });
  const rel = (p) => {
    const r = wrapRef.current.getBoundingClientRect();
    return { x: p.x - r.left, y: p.y - r.top };
  };

  const onTouchStart = (e) => {
    if (!isMobile) return;
    if (e.touches.length === 2) {
      const m = rel(mid(e.touches[0], e.touches[1]));
      gesture.current = { mode: "pinch", startDist: dist(e.touches[0], e.touches[1]), startScale: t.scale, startX: t.x, startY: t.y, m };
    } else if (e.touches.length === 1) {
      gesture.current = { mode: "pan", sx: e.touches[0].clientX - t.x, sy: e.touches[0].clientY - t.y, moved: false };
    }
  };

  const onTouchMove = (e) => {
    if (!isMobile || !gesture.current) return;
    const g = gesture.current;
    if (g.mode === "pinch" && e.touches.length === 2) {
      e.preventDefault();
      const ratio = dist(e.touches[0], e.touches[1]) / g.startDist;
      const scale = clamp(g.startScale * ratio, MIN_SCALE, MAX_SCALE);
      const p = { x: (g.m.x - g.startX) / g.startScale, y: (g.m.y - g.startY) / g.startScale };
      const c = clampPan(scale, g.m.x - p.x * scale, g.m.y - p.y * scale);
      setT({ scale, ...c });
    } else if (g.mode === "pan" && e.touches.length === 1) {
      const nx = e.touches[0].clientX - g.sx, ny = e.touches[0].clientY - g.sy;
      if (Math.abs(nx - t.x) > 3 || Math.abs(ny - t.y) > 3) g.moved = true;
      if (g.moved) { e.preventDefault(); setT((prev) => ({ scale: prev.scale, ...clampPan(prev.scale, nx, ny) })); }
    }
  };

  const onTouchEnd = () => { gesture.current = null; };
  const resetView = () => setT({ scale: 1, x: 0, y: 0 });

  const settings = info?.settings || {};
  const flavors = info?.flavors || [];
  const cx = settings.map_cart_x ?? 48;
  const cy = settings.map_cart_y ?? 42;

  const STORES = [
    { name: "Long Island City", address: "5241 Center Blvd, Long Island City", x: 82, y: 53 },
    { name: "Greenpoint", address: "227 West Street, Greenpoint", x: 89, y: 64 },
  ];

  return (
    <section id="map" className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="eyebrow mb-5">Santa Chiara · Authentic Italian Gelato</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-sc-espresso">
            Find a little <em className="font-light italic text-sc-bordeaux">dolce vita</em> somewhere in{" "}
            <span className="text-sc-gold">Manhattan</span>.
          </h1>
          <p className="mt-6 text-base md:text-lg max-w-xl leading-relaxed text-sc-espresso/80">
            Our roaming gelato cart pops up across New York City. Tap the little cart on the map to
            see exactly where we are today, our flavors, and our hours.
          </p>
        </motion.div>
      </div>

      <div className="w-full px-0 md:px-6 lg:px-10 md:flex md:justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative mt-8 md:mt-12 md:rounded-[2rem] overflow-hidden md:border-4 border-white bg-sc-cream shadow-[0_20px_60px_rgba(44,42,40,0.18)]"
          data-testid="manhattan-map"
        >
          <div
            ref={wrapRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className="overflow-hidden md:overflow-visible w-full md:w-auto touch-none md:touch-auto"
          >
            <div
              ref={innerRef}
              className="relative w-full md:w-fit md:mx-auto origin-top-left md:!transform-none"
              style={{ transform: isMobile ? `translate(${t.x}px, ${t.y}px) scale(${t.scale})` : undefined }}
            >
              <img
                src={ASSETS.mangaMap}
                alt="Illustrated manga-style vertical map of Manhattan"
                className="block w-full h-auto md:w-auto md:h-[118vh] select-none pointer-events-none"
                draggable={false}
              />

              {/* Cart marker */}
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    className="absolute -translate-x-1/2 -translate-y-1/2 group z-20"
                    style={{ left: `${cx}%`, top: `${cy}%` }}
                    data-testid="map-cart-icon"
                    aria-label="View gelato cart location"
                  >
                    <motion.span
                      animate={{ y: [0, -8, 0] }}
                      transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
                      className="relative block"
                    >
                      <img
                        src={ASSETS.cartIcon}
                        alt="Santa Chiara gelato cart"
                        className="w-20 sm:w-24 md:w-28 drop-shadow-[0_8px_16px_rgba(44,42,40,0.45)] group-hover:scale-110 transition-transform"
                        draggable={false}
                      />
                    </motion.span>
                    <span className="absolute left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap px-3 py-1 rounded-full bg-sc-espresso text-sc-cream text-[0.7rem] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      We're here! Tap me
                    </span>
                  </button>
                </SheetTrigger>

                <SheetContent className="bg-sc-cream w-full sm:max-w-md overflow-y-auto" data-testid="cart-detail-sheet">
                  <SheetHeader>
                    <div className="flex items-center gap-3">
                      <span className="grid place-items-center w-11 h-11 rounded-full bg-sc-espresso text-sc-cream">
                        <IceCream2 size={22} strokeWidth={1.6} />
                      </span>
                      <SheetTitle className="font-serif-display text-2xl text-sc-espresso text-left">
                        The Cart Today
                      </SheetTitle>
                    </div>
                    <SheetDescription className="text-left text-sc-espresso/60">
                      Where to find Santa Chiara right now, plus today's flavors and hours.
                    </SheetDescription>
                  </SheetHeader>

                  <div className="mt-8 space-y-7">
                    <Detail icon={<MapPin size={18} />} label="Current Location">
                      <p className="font-serif-display text-xl text-sc-espresso" data-testid="cart-current-location">
                        {settings.current_location || "Location updating soon"}
                      </p>
                    </Detail>
                    <Detail icon={<Clock size={18} />} label="Today's Hours">
                      <p className="text-sc-espresso/80">{settings.current_time || "—"}</p>
                    </Detail>
                    <Detail icon={<Megaphone size={18} />} label="Special Note">
                      <p className="text-sc-espresso/80 italic">{settings.current_notes || "—"}</p>
                    </Detail>
                    <div>
                      <div className="eyebrow mb-3">Flavors on the Cart</div>
                      <div className="flex flex-wrap gap-2" data-testid="cart-flavors">
                        {flavors.length === 0 && <span className="text-sc-espresso/60 text-sm">Flavors updating…</span>}
                        {flavors.map((f) => (
                          <span key={f.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-sm text-sc-espresso border border-sc-gold/20">
                            <span className="w-3 h-3 rounded-full" style={{ background: f.color }} />
                            {f.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {STORES.map((s) => (
                <div
                  key={s.name}
                  className="absolute -translate-x-1/2 -translate-y-full z-10 flex flex-col items-center group/store"
                  style={{ left: `${s.x}%`, top: `${s.y}%` }}
                  title={`Santa Chiara · ${s.address}`}
                  data-testid={`store-marker-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <GoldCone />
                  <div className="-mt-0.5 flex items-center px-2.5 py-1 rounded-full bg-sc-espresso shadow-[0_4px_10px_rgba(44,42,40,0.4)] border border-sc-goldlight/40">
                    <img src="/logo-santachiara.png" alt="Santa Chiara" className="h-2.5 sm:h-3 w-auto" />
                  </div>
                  <span className="mt-1 px-2 py-0.5 rounded-full bg-white/90 text-[0.6rem] sm:text-[0.66rem] font-semibold text-sc-espresso whitespace-nowrap shadow-sm">
                    {s.name}
                  </span>
                  <span className="mt-1 px-2 py-0.5 rounded-full bg-sc-gold text-sc-cream text-[0.58rem] font-medium whitespace-nowrap opacity-0 group-hover/store:opacity-100 transition-opacity pointer-events-none">
                    {s.address}
                  </span>
                </div>
              ))}

              {/* Governors Island — QC Spa marker */}
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    className="absolute -translate-x-1/2 -translate-y-1/2 z-20 group/gi flex flex-col items-center"
                    style={{ left: "58%", top: "93%" }}
                    data-testid="governors-island-marker"
                    aria-label="QC Spa New York on Governors Island"
                  >
                    <motion.span
                      animate={{ y: [0, -8, 0] }}
                      transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
                      className="block"
                    >
                      <img
                        src={ASSETS.cartIcon}
                        alt="QC Spa gelato cart"
                        className="w-16 sm:w-20 md:w-24 drop-shadow-[0_8px_14px_rgba(44,42,40,0.45)] group-hover/gi:scale-110 transition-transform"
                        draggable={false}
                      />
                    </motion.span>
                    <span className="-mt-1 px-3 py-1 rounded-full bg-sc-espresso text-sc-cream text-[0.66rem] sm:text-xs font-semibold whitespace-nowrap shadow-[0_4px_10px_rgba(44,42,40,0.4)]">
                      Governors Island
                    </span>
                  </button>
                </SheetTrigger>

                <SheetContent className="bg-sc-cream w-full sm:max-w-md overflow-y-auto" data-testid="qc-spa-sheet">
                  <SheetHeader>
                    <div className="flex items-center gap-3">
                      <span className="grid place-items-center w-11 h-11 rounded-full bg-sc-espresso text-sc-cream">
                        <IceCream2 size={22} strokeWidth={1.6} />
                      </span>
                      <SheetTitle className="font-serif-display text-2xl text-sc-espresso text-left">
                        QC SPA New York
                      </SheetTitle>
                    </div>
                    <SheetDescription className="text-left text-sc-espresso/60">
                      Find Santa Chiara gelato at QC New York on Governors Island — a day-spa retreat in the heart of the harbor.
                    </SheetDescription>
                  </SheetHeader>

                  <div className="mt-8 space-y-6">
                    <Detail icon={<MapPin size={18} />} label="Location">
                      <p className="font-serif-display text-xl text-sc-espresso">Governors Island, New York</p>
                    </Detail>
                    <a
                      href="https://www.qcny.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="qc-spa-book-btn"
                      className="inline-flex items-center justify-center w-full px-6 py-3 rounded-full bg-sc-espresso text-sc-cream text-sm font-semibold tracking-wide hover:bg-sc-bordeaux transition-colors"
                    >
                      BOOK
                    </a>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="md:hidden absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur text-[0.7rem] font-medium text-sc-espresso pointer-events-none">
            <ZoomIn size={13} /> Pinch to zoom · drag to explore
          </div>

          <div className="hidden md:flex absolute top-5 right-5 items-center gap-1.5 px-4 py-2 rounded-full bg-white/85 backdrop-blur text-xs font-medium text-sc-espresso pointer-events-none">
            <ZoomIn size={14} /> Tap the cart or a cone for details
          </div>

          {isMobile && Math.abs(t.scale - 1) > 0.01 && (
            <button
              onClick={resetView}
              data-testid="map-reset-btn"
              className="md:hidden absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-2 rounded-full bg-sc-espresso text-sc-cream text-xs font-medium shadow-lg active:scale-95 transition-transform"
            >
              <RotateCcw size={14} /> Reset
            </button>
          )}

          <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur text-xs font-medium text-sc-espresso">
            <span className="w-2 h-2 rounded-full bg-sc-sage animate-pulse" /> Live cart location
          </div>
        </motion.div>
      </div>

      <div className="flex justify-center mt-8 text-sc-espresso/50">
        <ChevronDown className="animate-bounce" />
      </div>
    </section>
  );
}

function GoldCone() {
  return (
    <svg viewBox="0 0 48 70" className="w-8 sm:w-10 h-auto drop-shadow-[0_4px_8px_rgba(44,42,40,0.45)]" aria-hidden="true">
      <defs>
        <linearGradient id="sc-gold-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F0D592" />
          <stop offset="0.55" stopColor="#D4B989" />
          <stop offset="1" stopColor="#B89968" />
        </linearGradient>
      </defs>
      {/* waffle cone */}
      <polygon points="12,30 36,30 24,68" fill="url(#sc-gold-grad)" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
      <line x1="18" y1="34" x2="27" y2="60" stroke="#fff" strokeWidth="1" opacity="0.5" />
      <line x1="26" y1="34" x2="22" y2="48" stroke="#fff" strokeWidth="1" opacity="0.5" />
      {/* soft-serve swirl scoops */}
      <circle cx="24" cy="28" r="12" fill="url(#sc-gold-grad)" stroke="#fff" strokeWidth="2" />
      <circle cx="24" cy="18" r="9.5" fill="url(#sc-gold-grad)" stroke="#fff" strokeWidth="2" />
      <circle cx="24" cy="9.5" r="6.5" fill="url(#sc-gold-grad)" stroke="#fff" strokeWidth="2" />
      <circle cx="24" cy="4.5" r="2.6" fill="url(#sc-gold-grad)" stroke="#fff" strokeWidth="1.5" />
    </svg>
  );
}

function Detail({ icon, label, children }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 text-sc-gold">{icon}</span>
      <div>
        <div className="eyebrow mb-1">{label}</div>
        {children}
      </div>
    </div>
  );
}
